import {
  HttpEvent,
  HttpEventType,
  HttpProgressEvent,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { distinctUntilChanged, scan } from 'rxjs/operators';

function isHttpResponse<T>(event: HttpEvent<T>): event is HttpResponse<T> {
  return event.type === HttpEventType.Response;
}

function isHttpProgressEvent(
  event: HttpEvent<unknown>
): event is HttpProgressEvent {
  return (
    event.type === HttpEventType.DownloadProgress ||
    event.type === HttpEventType.UploadProgress
  );
}

export interface DownloadContentWrapper {
  content: Blob | null;
  progress: number;
  state: 'PENDING' | 'IN_PROGRESS' | 'DONE';
}

/** custom operator to monitor the progress of a file download based on Http events and act upon specific Http event */
export function download(
  saver?: (b: Blob, filename: string) => void
): (source: Observable<HttpEvent<Blob>>) => Observable<DownloadContentWrapper> {
  return (source: Observable<HttpEvent<Blob>>) =>
    source.pipe(
      scan(
        (
          downloadContentWrapper: DownloadContentWrapper,
          event
        ): DownloadContentWrapper => {
          if (isHttpProgressEvent(event)) {
            return {
              progress: event.total
                ? Math.round((100 * event.loaded) / event.total)
                : downloadContentWrapper.progress,
              state: 'IN_PROGRESS',
              content: null,
            };
          }
          if (isHttpResponse(event)) {
            if (saver) {
              const filenameForDownload = event.headers
                .get('Content-Disposition')
                .split(';')[1]
                .split('filename')[1]
                .split('=')[1]
                .trim()
                .replace(/\"/g, '');
              saver(event.body, filenameForDownload);
            }
            return {
              progress: 100,
              state: 'DONE',
              content: event.body,
            };
          }
          return downloadContentWrapper;
        },
        { state: 'PENDING', progress: 0, content: null }
      ),
      distinctUntilChanged(
        (a, b) =>
          a.state === b.state &&
          a.progress === b.progress &&
          a.content === b.content
      )
    );
}
